import React, { useEffect, useState } from 'react';
import { Modal } from '../../components/Modal';
import { replyToInboxEmail, type InboxEmailDetail } from '../../lib/inboxApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The message being replied to. Its `from` and `subject` seed the form. */
  email: InboxEmailDetail;
  /** The logged outbound email the server answers with. */
  onSent: (reply: InboxEmailDetail) => void;
}

/** Contract: up to 10 files, 20 MB each. Refused here so a typed reply is not lost to a 413. */
const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/**
 * `Re:` once, never twice. Mail clients hand back "Re: Re: Re: quote" otherwise, and this
 * thread's subject is what the server groups messages by.
 */
export function replySubject(original?: string): string {
  const subject = String(original || '').trim();
  if (!subject) return '';
  return /^re:/i.test(subject) ? subject : `Re: ${subject}`;
}

export const ReplyComposeModal: React.FC<Props> = ({ isOpen, onClose, email, onSent }) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed on open, and when the user moves to another message with the composer already
  // mounted. Deliberately NOT keyed on anything that changes mid-compose: a re-seed would
  // wipe what the user has typed.
  useEffect(() => {
    if (!isOpen) return;
    setTo(String(email?.from || '').trim());
    setCc('');
    setSubject(replySubject(email?.subject));
    setText('');
    setAttachments([]);
    setError(null);
  }, [isOpen, email?._id, email?.from, email?.subject]);

  const handleFilesChange = (files: FileList | null) => {
    const picked = Array.from(files || []);
    if (picked.length > MAX_ATTACHMENTS) {
      setError(`Too many attachments. Maximum is ${MAX_ATTACHMENTS} files.`);
      return;
    }
    const tooBig = picked.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig) {
      setError(`"${tooBig.name}" is too large. Maximum allowed is 20 MB per file.`);
      return;
    }
    setError(null);
    setAttachments(picked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard as well as `disabled`: a second submit while the first is in flight would send
    // the email twice, and there is no unsending it.
    if (sending || !to.trim()) return;

    setSending(true);
    setError(null);

    try {
      const reply = await replyToInboxEmail(email._id, {
        to: to.trim(),
        cc: cc.trim() || undefined,
        subject: subject.trim() || undefined,
        text,
        attachments,
      });
      setSending(false);
      onSent(reply);
      onClose();
    } catch (err: any) {
      // The modal stays open with everything still typed in it. Losing a written reply to a
      // transient SMTP failure (code EMAIL_SEND_FAILED) is the one unforgivable outcome here.
      setSending(false);
      setError(err?.message || 'Failed to send reply');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { if (!sending) onClose(); }} title="Reply" maxWidthClass="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-3 text-xs">
        <div>
          <label className="block mb-1 text-xs font-medium text-slate-600" htmlFor="reply-to">
            To
          </label>
          <input
            id="reply-to"
            className="w-full h-9 border border-slate-200 rounded-sm px-2 text-xs bg-white"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="name@example.com, second@example.com"
          />
        </div>

        <div>
          <label className="block mb-1 text-xs font-medium text-slate-600" htmlFor="reply-cc">
            Cc
          </label>
          <input
            id="reply-cc"
            className="w-full h-9 border border-slate-200 rounded-sm px-2 text-xs bg-white"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div>
          <label className="block mb-1 text-xs font-medium text-slate-600" htmlFor="reply-subject">
            Subject
          </label>
          <input
            id="reply-subject"
            className="w-full h-9 border border-slate-200 rounded-sm px-2 text-xs bg-white"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1 text-xs font-medium text-slate-600" htmlFor="reply-body">
            Message
          </label>
          <textarea
            id="reply-body"
            className="w-full border border-slate-200 rounded-sm px-2 py-1.5 text-xs bg-white min-h-[140px]"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write your reply..."
          />
        </div>

        <div>
          <label className="block mb-1 text-xs font-medium text-slate-600">Attachments</label>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center px-3 h-8 bg-slate-50 border border-slate-300 rounded-sm text-xs cursor-pointer hover:bg-slate-100">
              Choose Files
              <input
                type="file"
                multiple
                className="hidden"
                aria-label="Attachments"
                onChange={(e) => handleFilesChange(e.target.files)}
              />
            </label>
            <span className="text-xs text-slate-500 truncate">
              {attachments.length
                ? attachments.map((f) => f.name).join(', ')
                : 'No files chosen'}
            </span>
          </div>
        </div>

        {error && <div className="text-xs text-red-500">{error}</div>}

        <div className="pt-4">
          <button
            type="submit"
            disabled={sending || !to.trim()}
            className="w-full py-3 rounded-md bg-primary-500 border-b-4 border-primary-600 text-white text-sm font-bold hover:bg-primary-600 active:border-b-0 active:mt-1 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending…' : 'Send reply'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
